/**
 * HIPAA Authorization Templates v4.0-2026-04
 *
 * Changes from v3:
 * - Removed named individual ("Corbin Ebert") from the Authorized Representative
 *   clause. Authorization is now granted to mediloop inc. directly.
 *
 * Note: v5 is reserved for the updated template language (see uploaded docx,
 * April 2026) and will be activated once the Delaware name change is confirmed.
 *
 * Three variants:
 * - selfBody: Patient signing for themselves
 * - childBody: Parent/guardian signing for a minor child
 * - spouseBody: Spouse signing for themselves (sent via email or device handoff)
 *
 * All variants include the Legal Attestation clause.
 */

export const HIPAA_V4_VERSION = 'v4.0-2026-04';

import { ATTESTATION_PARENT, ATTESTATION_SELF } from './v3';
export { ATTESTATION_PARENT, ATTESTATION_SELF, ATTESTATION_TEXT } from './v3';

const SELF_BODY = `
AUTHORIZATION FOR RELEASE OF PROTECTED HEALTH INFORMATION
(HIPAA AUTHORIZATION & INSURANCE COMMUNICATION AUTHORIZATION)

1. PATIENT INFORMATION
Patient Name: {{full_name}}
Date of Birth: {{date_of_birth}}

2. AUTHORIZED REPRESENTATIVE
I authorize the disclosure of my Protected Health Information ("PHI") to:

mediloop inc., who is authorized to act on behalf of the Company in connection with medical billing review, audit, and negotiation services.

Email: help@mediloop.ai
Website: https://www.mediloop.ai

(collectively referred to as the "Authorized Representative")

3. UNIVERSAL HEALTHCARE PROVIDER AUTHORIZATION
I authorize any and all physicians, hospitals, clinics, pharmacies, laboratories, and other healthcare providers ("Healthcare Providers") to disclose my PHI to the Authorized Representative.

4. INSURANCE AUTHORIZATION
I authorize any and all health insurance companies, health plans, and payers (including, but not limited to, private insurers, employer-sponsored plans, Medicare, and Medicaid) to speak with and provide information to the Authorized Representative regarding:
1. Claims filed on my behalf
2. Explanations of Benefits (EOBs)
3. Coverage determinations and benefit details
4. Denial reasons, adjustment amounts, and billing codes

5. INFORMATION AUTHORIZED FOR DISCLOSURE
This authorization permits disclosure of the following information, to the extent reasonably necessary for billing review and negotiation:
1. Medical billing records, including UB-04 and CMS-1500 forms
2. Itemized statements and account histories
3. CPT, ICD-10, and related billing or procedure codes
4. Limited clinical documentation required solely to verify billing and coding accuracy

This authorization does not permit disclosure of psychotherapy notes unless otherwise required by law.

6. PURPOSE OF DISCLOSURE
The purpose of this authorization is to allow the Authorized Representative to:
1. Audit and review medical bills
2. Verify billing and coding accuracy
3. Communicate with healthcare providers and insurers
4. Attempt to negotiate or resolve disputed medical charges

7. DATES OF SERVICE
This authorization applies to all dates of service from January 1, 2020 through December 31, 2030, unless earlier revoked.

8. EXPIRATION & REVOCATION
Expiration
This authorization expires on the earlier of:
(a) termination of my relationship with mediloop inc., or
(b) December 31, 2030.

Revocation
I understand that I may revoke this authorization at any time by providing written notice to mediloop inc. Revocation will not affect disclosures already made in reliance on this authorization.

9. REDISCLOSURE NOTICE
I understand that information disclosed pursuant to this authorization may be subject to redisclosure and may no longer be protected by HIPAA, except as otherwise required by law.

10. LEGAL ATTESTATION
${ATTESTATION_SELF}

11. SIGNATURE
I have read and understand this authorization and voluntarily consent to the disclosure of my information as described above.

Signature of Patient or Legal Representative: {{signature}}
Printed Name: {{printed_name}}
Date: {{today}}
`;

const CHILD_BODY = `
AUTHORIZATION FOR RELEASE OF PROTECTED HEALTH INFORMATION
(HIPAA AUTHORIZATION & INSURANCE COMMUNICATION AUTHORIZATION)
MINOR PATIENT — SIGNED BY PARENT/LEGAL GUARDIAN

1. PATIENT INFORMATION
Patient Name: {{patient_name}}
Patient Date of Birth: {{patient_dob}}

Personal Representative: {{parent_name}} (Parent/Legal Guardian)

2. AUTHORIZED REPRESENTATIVE
I, {{parent_name}}, as the legal parent/guardian of the minor patient named above, authorize the disclosure of my child's Protected Health Information ("PHI") to:

mediloop inc., who is authorized to act on behalf of the Company in connection with medical billing review, audit, and negotiation services.

Email: help@mediloop.ai
Website: https://www.mediloop.ai

(collectively referred to as the "Authorized Representative")

3. UNIVERSAL HEALTHCARE PROVIDER AUTHORIZATION
I authorize any and all physicians, hospitals, clinics, pharmacies, laboratories, and other healthcare providers ("Healthcare Providers") to disclose the above-named patient's PHI to the Authorized Representative.

4. INSURANCE AUTHORIZATION
I authorize any and all health insurance companies, health plans, and payers (including, but not limited to, private insurers, employer-sponsored plans, Medicare, and Medicaid) to speak with and provide information to the Authorized Representative regarding:
1. Claims filed on behalf of the above-named patient
2. Explanations of Benefits (EOBs)
3. Coverage determinations and benefit details
4. Denial reasons, adjustment amounts, and billing codes

5. INFORMATION AUTHORIZED FOR DISCLOSURE
This authorization permits disclosure of the following information, to the extent reasonably necessary for billing review and negotiation:
1. Medical billing records, including UB-04 and CMS-1500 forms
2. Itemized statements and account histories
3. CPT, ICD-10, and related billing or procedure codes
4. Limited clinical documentation required solely to verify billing and coding accuracy

This authorization does not permit disclosure of psychotherapy notes unless otherwise required by law.

6. PURPOSE OF DISCLOSURE
The purpose of this authorization is to allow the Authorized Representative to:
1. Audit and review medical bills
2. Verify billing and coding accuracy
3. Communicate with healthcare providers and insurers
4. Attempt to negotiate or resolve disputed medical charges

7. DATES OF SERVICE
This authorization applies to all dates of service from January 1, 2020 through December 31, 2030, unless earlier revoked.

8. EXPIRATION & REVOCATION
Expiration
This authorization expires on the earlier of:
(a) termination of the parent/guardian's relationship with mediloop inc., or
(b) December 31, 2030, or
(c) the minor patient reaching the age of majority in their state of residence.

Revocation
I understand that I may revoke this authorization at any time by providing written notice to mediloop inc. Revocation will not affect disclosures already made in reliance on this authorization.

9. REDISCLOSURE NOTICE
I understand that information disclosed pursuant to this authorization may be subject to redisclosure and may no longer be protected by HIPAA, except as otherwise required by law.

10. LEGAL ATTESTATION
${ATTESTATION_PARENT}

11. SIGNATURE OF PARENT/LEGAL GUARDIAN
I have read and understand this authorization. I certify that I am the legal parent or guardian of the minor patient named above and have the authority to authorize the release of their medical information.

Signature of Parent/Legal Guardian: {{signature}}
Printed Name: {{printed_name}}
Relationship to Patient: Parent/Legal Guardian
Date: {{today}}
`;

const SPOUSE_BODY = `
AUTHORIZATION FOR RELEASE OF PROTECTED HEALTH INFORMATION
(HIPAA AUTHORIZATION & INSURANCE COMMUNICATION AUTHORIZATION)

1. PATIENT INFORMATION
Patient Name: {{full_name}}
Date of Birth: {{date_of_birth}}

2. AUTHORIZED REPRESENTATIVE
I authorize the disclosure of my Protected Health Information ("PHI") to:

mediloop inc., who is authorized to act on behalf of the Company in connection with medical billing review, audit, and negotiation services.

Email: help@mediloop.ai
Website: https://www.mediloop.ai

(collectively referred to as the "Authorized Representative")

This authorization is being submitted in connection with a mediloop account held by {{account_holder_name}}.

3. UNIVERSAL HEALTHCARE PROVIDER AUTHORIZATION
I authorize any and all physicians, hospitals, clinics, pharmacies, laboratories, and other healthcare providers ("Healthcare Providers") to disclose my PHI to the Authorized Representative.

4. INSURANCE AUTHORIZATION
I authorize any and all health insurance companies, health plans, and payers (including, but not limited to, private insurers, employer-sponsored plans, Medicare, and Medicaid) to speak with and provide information to the Authorized Representative regarding:
1. Claims filed on my behalf
2. Explanations of Benefits (EOBs)
3. Coverage determinations and benefit details
4. Denial reasons, adjustment amounts, and billing codes

5. INFORMATION AUTHORIZED FOR DISCLOSURE
This authorization permits disclosure of the following information, to the extent reasonably necessary for billing review and negotiation:
1. Medical billing records, including UB-04 and CMS-1500 forms
2. Itemized statements and account histories
3. CPT, ICD-10, and related billing or procedure codes
4. Limited clinical documentation required solely to verify billing and coding accuracy

This authorization does not permit disclosure of psychotherapy notes unless otherwise required by law.

6. PURPOSE OF DISCLOSURE
The purpose of this authorization is to allow the Authorized Representative to:
1. Audit and review medical bills
2. Verify billing and coding accuracy
3. Communicate with healthcare providers and insurers
4. Attempt to negotiate or resolve disputed medical charges

7. DATES OF SERVICE
This authorization applies to all dates of service from January 1, 2020 through December 31, 2030, unless earlier revoked.

8. EXPIRATION & REVOCATION
Expiration
This authorization expires on the earlier of:
(a) termination of my relationship with mediloop inc., or
(b) December 31, 2030.

Revocation
I understand that I may revoke this authorization at any time by providing written notice to mediloop inc. Revocation will not affect disclosures already made in reliance on this authorization.

9. REDISCLOSURE NOTICE
I understand that information disclosed pursuant to this authorization may be subject to redisclosure and may no longer be protected by HIPAA, except as otherwise required by law.

10. LEGAL ATTESTATION
${ATTESTATION_SELF}

11. SIGNATURE
I have read and understand this authorization and voluntarily consent to the disclosure of my information as described above.

Signature of Patient: {{signature}}
Printed Name: {{printed_name}}
Date: {{today}}
`;

export const HIPAA_V4 = {
  version: HIPAA_V4_VERSION,
  selfBody: SELF_BODY,
  childBody: CHILD_BODY,
  spouseBody: SPOUSE_BODY,
};
